/**
 * Follow-Up Service
 *
 * API calls for follow-up functionality.
 */

import { environment } from '../env/environment';
import { secureSessionStorage } from '../utils/secure-session';
import logger from '../utils/logger';

const API_BASE = `${environment.baseUrl}/api/followup`;

/** Adds the session from verifying the patient's code; the server needs it to read or change their data. */
const withSession = async (headers) => {
  const sessionId = await secureSessionStorage.getItem('otp_session_id');
  if (sessionId) headers['X-Session-ID'] = sessionId;
  return headers;
};

/**
 * Check if email exists in database (determines follow-up vs new user)
 * @param {string} email - User email
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @returns {Promise<Object>} { success, exists, submission? }
 */
export const checkEmailExists = async (email, tenantId) => {
  // Build headers with tenant ID for proper isolation
  const headers = {
    'Content-Type': 'application/json',
  };

  // Get tenant ID from params, window, or localStorage
  const windowTenantId = window.__TENANT_ID__;
  const localStorageTenantId = localStorage.getItem('followup_tenant_id');
  const resolvedTenantId = tenantId || windowTenantId || localStorageTenantId;

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = String(resolvedTenantId);
  } else {
    logger.warn('🔗 [followupService] ⚠️ No tenant ID found - check-email will query ALL tenants!');
  }

  const response = await fetch(`${API_BASE}/check-email`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to check email');
  }

  return data;
};

/**
 * Get submission data by email (for prefilling)
 * @param {string} email - User email
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @returns {Promise<Object>} Submission data with form data and modules
 */
export const getSubmissionDataByEmail = async (email, tenantId) => {
  // Build headers with tenant ID for proper isolation
  const headers = {
    'Content-Type': 'application/json',
  };

  // Get tenant ID from params, window, or localStorage
  const windowTenantId = window.__TENANT_ID__;
  const localStorageTenantId = localStorage.getItem('followup_tenant_id');
  const resolvedTenantId = tenantId || windowTenantId || localStorageTenantId;

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = String(resolvedTenantId);
  } else {
    logger.warn('🔗 [followupService] ⚠️ No tenant ID found - submission-data will query ALL tenants!');
  }

  const response = await fetch(`${API_BASE}/submission-data/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: await withSession(headers),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch submission data');
  }

  return data;
};

/**
 * Update selected modules with new data
 * @param {Object} params - Update parameters
 * @param {string} params.submissionId - The submission ID
 * @param {Array} params.selectedModules - List of selected module IDs
 * @param {Object} params.updatedFields - Updated field data
 * @param {string|number} params.tenantId - The tenant ID for webhook routing
 * @returns {Promise<Object>} Update result
 */
export const updateModules = async ({ submissionId, selectedModules, updatedFields, tenantId }) => {
  // Build headers with tenant ID and session ID for proper auth
  const headers = {
    'Content-Type': 'application/json',
  };

  // Get tenant ID from params, window, or localStorage
  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = resolvedTenantId;
  }

  // Attach OTP session ID for server-side access verification
  const sessionId = await secureSessionStorage.getItem('otp_session_id');
  if (sessionId) {
    headers['X-Session-ID'] = sessionId;
  }

  const response = await fetch(`${API_BASE}/update`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      submissionId,
      selectedModules,
      updatedFields,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to update modules');
  }

  return data;
};

/**
 * Create a new submission for new users entering via follow-up flow
 * @param {string} email - Patient email
 * @param {Object} formData - Complete form data
 * @param {string|number} tenantId - Tenant ID
 * @returns {Promise<Object>} { success, submissionId, accessToken }
 */
export const createNewSubmission = async (email, formData, tenantId) => {
  const headers = { 'Content-Type': 'application/json' };
  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');
  if (resolvedTenantId) headers['X-Tenant-ID'] = String(resolvedTenantId);

  const response = await fetch(`${API_BASE}/create-submission`, {
    method: 'POST',
    headers: await withSession(headers),
    body: JSON.stringify({ email, formData }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to create submission');
  }
  return data;
};

/**
 * Generate and upload follow-up PDF
 * @param {Object} params - PDF generation parameters
 * @param {string} params.submissionId - The submission ID
 * @param {Blob} params.pdfBlob - The form PDF blob
 * @param {Blob} params.painDiagramBlob - The pain diagram PDF blob (optional)
 * @param {string|number} params.tenantId - The tenant ID for proper isolation
 * @returns {Promise<Object>} Generated PDF info
 */
export const generateFollowUpPDF = async ({ submissionId, pdfBlob, painDiagramBlob, tenantId }) => {
  const formData = new FormData();
  formData.append('submissionId', submissionId);
  if (pdfBlob) {
    formData.append('pdf', pdfBlob, 'form.pdf');
  }
  if (painDiagramBlob) {
    formData.append('painDiagramPdf', painDiagramBlob, 'painDiagram.pdf');
  }

  // Build headers with tenant ID for proper isolation
  const headers = {};

  // Get tenant ID from params, window, or localStorage
  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = resolvedTenantId;
  }

  const response = await fetch(`${API_BASE}/generate-pdf`, {
    method: 'POST',
    headers: await withSession(headers),
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to generate PDF');
  }

  return data;
};

/**
 * Get follow-up history for a submission
 * @param {string} submissionId - Submission UUID
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @returns {Promise<Object>} Follow-up history
 */
export const getFollowUpHistory = async (submissionId, tenantId) => {
  // Build headers with tenant ID for proper isolation
  const headers = {};

  // Get tenant ID from params, window, or localStorage
  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = resolvedTenantId;
  }

  const response = await fetch(`${API_BASE}/history/${submissionId}`, {
    method: 'GET',
    headers: await withSession(headers),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch history');
  }

  return data;
};

/**
 * Trigger first step webhook when user selects modules and proceeds
 * @param {Object} params - Webhook parameters
 * @param {string} params.submissionId - The submission ID
 * @param {Array} params.selectedModules - List of selected module IDs
 * @param {string|number} params.tenantId - The tenant ID for proper isolation
 * @returns {Promise<Object>} Webhook trigger result
 */
export const triggerFirstStepWebhook = async ({ submissionId, selectedModules, tenantId }) => {
  // Build headers with tenant ID for proper isolation
  const headers = {
    'Content-Type': 'application/json',
  };

  // Get tenant ID from params, window, or localStorage
  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = resolvedTenantId;
  }

  const response = await fetch(`${API_BASE}/first-step-webhook`, {
    method: 'POST',
    headers: await withSession(headers),
    body: JSON.stringify({
      submissionId,
      selectedModules,
    }),
  });

  const data = await response.json();

  // Don't throw error if webhook fails - it's not critical
  if (!response.ok) {
    logger.warn('First step webhook failed:', data.message);
  }

  return data;
};

/**
 * Request OTP for follow-up verification
 * @param {string} email - User email
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @param {string} method - 'email' or 'phone'
 * @param {string} phone - Phone number (required if method is 'phone')
 * @returns {Promise<Object>} { success, message, expiresIn, attemptsRemaining, smsConfigured }
 */
export const requestOtp = async (email, tenantId, method = 'email', phone = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = String(resolvedTenantId);
  }

  const body = { email, method };
  if (method === 'phone' && phone) {
    body.phone = phone;
  }

  const response = await fetch(`${API_BASE}/request-otp`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok && response.status !== 429) {
    throw new Error(data.message || 'Failed to request OTP');
  }

  return data;
};

/**
 * Verify OTP for follow-up access
 * @param {string} email - User email
 * @param {string} otp - OTP code entered by user
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @returns {Promise<Object>} { success, verified, sessionId?, attemptsRemaining? }
 */
export const verifyOtp = async (email, otp, tenantId) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = String(resolvedTenantId);
  }

  const response = await fetch(`${API_BASE}/verify-otp`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, otp }),
  });

  const data = await response.json();

  // Return data with both API status and HTTP status
  return {
    ...data,
    httpStatus: response.status,
  };
};

/**
 * Validate OTP session before proceeding
 * @param {string} email - User email
 * @param {string} sessionId - OTP session ID
 * @param {string|number} tenantId - The tenant ID for proper isolation (optional)
 * @returns {Promise<Object>} { success, valid }
 */
export const validateOtpSession = async (email, sessionId, tenantId) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const resolvedTenantId = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');

  if (resolvedTenantId) {
    headers['X-Tenant-ID'] = String(resolvedTenantId);
  }

  const response = await fetch(`${API_BASE}/validate-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, sessionId }),
  });

  const data = await response.json();

  return {
    ...data,
    status: response.status,
  };
};

/**
 * Check OTP/lockout status from server (source of truth)
 * Called on every mount of OTP page to reconcile after reload/navigation
 */
export const checkOtpStatus = async (identifier, tenantId) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const tid = tenantId || window.__TENANT_ID__ || localStorage.getItem('followup_tenant_id');
    if (tid) headers['X-Tenant-ID'] = String(tid);

    const response = await fetch(`${API_BASE}/otp-status`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ identifier }),
    });
    return await response.json();
  } catch (error) {
    logger.error('[followupService] OTP status check failed:', error.message);
    return { state: 'error' };
  }
};

export default {
  checkEmailExists,
  getSubmissionDataByEmail,
  updateModules,
  generateFollowUpPDF,
  getFollowUpHistory,
  triggerFirstStepWebhook,
  requestOtp,
  verifyOtp,
  validateOtpSession,
  checkOtpStatus,
};
